import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  EOrderStatus,
  IFetchListProps,
  IOrder,
  IOrdersSliceState,
} from "./types";
import { ApiStatuses, IDateRange, SortTypes } from "../../app/types";
import { RootState } from "../../app/store";
import {
  getOrder,
  getOrders,
} from "./api";

export const initialState: IOrdersSliceState = {
  list: [],
  selectedOrdersToProcess: [],
  filters: {
    page: 0,
    statusFilter: [],
    dateFilter: { start: null, end: null },
    dateFilterSort: "none",
    pageSize: 10,
    hasNext: false,
    hasPrevious: false,
    totalItems: 0,
    totalPages: 0,
  },
  status: ApiStatuses.initial,
  detailsStatus: ApiStatuses.initial,
  orderNotFound: false,
};

export const fetchList = createAsyncThunk(
  "orders/fetchList",
  async ({
    siteId,
    filters
  }: IFetchListProps) => {
    const response = await getOrders(
      siteId,
      filters
    );
    return response.data;
  }
);

export const refreshList = createAsyncThunk(
  "orders/refreshList",
  async (siteId: number, { getState }) => {
    const state = getState() as RootState;
    const { filters } = state.orders;
    const response = await getOrders(siteId, filters);
    return response.data;
  }
);

export const fetchOrder = createAsyncThunk(
  "orders/fetchOrder",
  async ({ orderId }: { orderId: string }) => {
    const response = await getOrder(orderId);
    return response.data;
  }
);

export const refreshOrder = createAsyncThunk(
  "orders/refreshOrder",
  async ({ orderId }: { orderId: string }) => {
    const response = await getOrder(orderId);
    return response.data;
  }
);

const slice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    setPage(state: IOrdersSliceState, action: PayloadAction<number>) {
      state.filters.page = action.payload;
    },
    setPageSize(state: IOrdersSliceState, action: PayloadAction<number>) {
      state.filters.pageSize = action.payload;
    },
    setFilters(
      state: IOrdersSliceState,
      action: PayloadAction<{
        statuses: string;
        dates: IDateRange;
      }>
    ) {
      const { statuses, dates } = action.payload;
      state.filters.statusFilter = statuses
        ? (statuses.split(",") as EOrderStatus[])
        : [];
      
      state.filters.dateFilter = dates;
      state.filters.page = 0;
    },
    setDateSort(state: IOrdersSliceState, action: PayloadAction<SortTypes>) {
      state.filters.dateFilterSort = action.payload;
    },
    clearFilters(state: IOrdersSliceState) {
      state.filters.statusFilter = [];
      state.filters.dateFilter = { start: null, end: null };
    },
    setSelectedOrdersToProcess(state: IOrdersSliceState, action: PayloadAction<IOrder[]>) {
      state.selectedOrdersToProcess = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchList
      .addCase(fetchList.pending, (state) => {
        state.status = ApiStatuses.loading;
      })
      .addCase(fetchList.fulfilled, (state, action) => {
        state.status = ApiStatuses.success;
        state.list = action.payload.data;
        state.filters.totalItems = action.payload.totalItems;
        state.filters.totalPages = action.payload.totalPages;
        state.filters.hasNext = state.filters.page < state.filters.totalPages;
        state.filters.hasPrevious = state.filters.page > 0;
      })
      .addCase(fetchList.rejected, (state) => {
        state.status = ApiStatuses.fail;
      })
      // refreshList
      .addCase(refreshList.fulfilled, (state, action) => {
        state.list = action.payload.data;
        state.filters.totalItems = action.payload.totalItems;
        state.filters.totalPages = action.payload.totalPages;
        state.filters.hasNext = state.filters.page < state.filters.totalPages;
        state.filters.hasPrevious = state.filters.page > 0;
      })
      // fetchOrder
      .addCase(fetchOrder.pending, (state) => {
        state.orderNotFound = false;
        state.detailsStatus = ApiStatuses.loading;
      })
      .addCase(fetchOrder.fulfilled, (state, action) => {
        state.detailsStatus = ApiStatuses.success;
        state.selectedOrder = action.payload;
      })
      .addCase(fetchOrder.rejected, (state, action) => {
        state.detailsStatus = ApiStatuses.fail;
        state.orderNotFound = !!action.error.message?.includes("404");
      })
      // refreshOrder
      .addCase(refreshOrder.fulfilled, (state, action) => {
        state.selectedOrder = action.payload;
      })
  },
});

export const orders = slice.reducer;
export const actions = slice.actions;
